import { useParams, useRouteMatch } from 'react-router'

import { useModalContext } from '@sketch/components'
import { isCwvSpecificRoute, routes, useFlag } from '@sketch/modules-common'
import { useVersioning } from 'modules/versioning'
import {
  useShareRenameMutation,
  useShareDescriptionChangeMutation,
  ShareAboutFragment,
} from '@sketch/gql-types'

import ShareSettingsModal from '../../modals/ShareSettingsModal'
import SharingModal from '../../modals/SharingModal'
import { AboutTabRouteParams } from './AboutTab.types'

export const useDocumentNameChange = () =>
  useShareRenameMutation({
    onError: 'show-toast',
    redirectErrors: true,
    optimisticResponse: ({ identifier, name }) => ({
      __typename: 'RootMutationType',
      shareUpdate: {
        __typename: 'ShareUpdatePayload',
        successful: true,
        share: {
          __typename: 'Share',
          identifier,
          name,
        },
        errors: [],
      },
    }),
  })[0]

export const useDocumentDescriptionChange = () =>
  useShareDescriptionChangeMutation({
    onError: 'show-toast',
    redirectErrors: true,
    optimisticResponse: ({ identifier, description }) => ({
      __typename: 'RootMutationType',
      shareUpdate: {
        __typename: 'ShareUpdatePayload',
        successful: true,
        share: {
          __typename: 'Share',
          identifier,
          description,
        },
        errors: [],
      },
    }),
  })[0]

/**
 * This method should be removed when we turn on and remove
 * the feature flag "new-sharing". Don't forget to also remove
 * the button that uses this method.
 */
export const useShareSettingsModal = () => {
  const { share, latestVersion } = useVersioning()
  const { showModal } = useModalContext()
  const isNewSharingOn = useFlag('new-sharing')

  if (isNewSharingOn) {
    return () => {
      showModal(SharingModal, { share, latestVersion })
    }
  }

  return () => {
    showModal(ShareSettingsModal, { share })
  }
}

/**
 * __usePagesURL__
 *
 * Generates the URL used for the "Pages" link in the About sidebar. The link
 * should open the first page in a document when viewing all pages, and do
 * nothing if the user was already looking at a specific page in the document.
 *
 * @param {ShareAboutFragment} share - shared document (SEE: `ShareAboutFragment` gql type)
 *
 * @returns {string} URL used in About sidebar "Pages" link
 */
export function usePagesURL(share: ShareAboutFragment): string {
  const params = useParams<AboutTabRouteParams>()
  const { path } = useRouteMatch()

  const { pages } = share.version?.document || {}
  const firstPageId = pages?.entries[0]?.uuid || ''
  const currentPageId = params?.pageUUID || ''

  const isDocumentView = isCwvSpecificRoute(path, 'SHARE_VIEW')

  return routes.SHARE_PAGE_CANVAS_VIEW.create({
    shareID: share.identifier,
    pageUUID: isDocumentView ? firstPageId : currentPageId,
  })
}
